import * as qs from 'qs'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import type { EventDescription } from '~/composables/types/event'
import type { TypesenseClientHandle } from '~/composables/search/useSearchClient'
import { useSearchEngine } from '~/composables/search/useSearchEngine'
import { useTypesenseHandle } from '~/composables/search/useTypesenseHandle'
import { fetchTypesenseSourceEvent } from '~/composables/search/useTypesenseSourceEvent'
import { getSimilarKitsQueryParams } from '~/composables/product'
import { kftApiRequest } from '~/utils/kftApiRequest'
import { transformToAlgoliaEvent } from '~/utils/helpers'
import { typesenseSearch } from '~/utils/typesenseClient'
import {
  buildTextSimilarKitsQuery,
  buildVectorSimilarKitsQuery,
  SIMILAR_KITS_LIMIT,
  type SimilarShowcaseSource,
} from '~/utils/typesenseSimilarEventsQuery'
import { getBoxesIndexName } from '~/utils/searchEngineHelpers'
import { toSearchEngineEventItem } from '~/utils/adaptTypesenseEventDocument'

/**
 * Kits within the page's categories, closest by meaning (KFT-4193). A kit page
 * searches from the kit's own stored vector; an event page from its title +
 * subtitle, embedded at query time (events aren't in `products_*`).
 */
const fetchSimilarKitsTypesense = async (
  page: SimilarShowcaseSource,
  collection: string,
  handle: TypesenseClientHandle
): Promise<SearchEngineEventItem[]> => {
  let params
  if (page.isKit) {
    const kit = await fetchTypesenseSourceEvent(
      page.permalink,
      collection,
      handle
    )
    if (!kit) return []
    params = buildVectorSimilarKitsQuery(kit.id, page.categories, collection)
  } else {
    if (!page.text) return []
    params = buildTextSimilarKitsQuery(page.text, page.categories, collection)
  }

  // Static per page, so every viewer of this page sends the same request.
  const result = await typesenseSearch<Record<string, unknown>>(params, {
    ...handle,
    useCache: true,
  })

  return (result.hits ?? []).map(({ document }) =>
    toSearchEngineEventItem(document)
  )
}

/** KFT API kits in the main category, minus the page's own kit. */
const fetchSimilarKitsKft = async (
  page: SimilarShowcaseSource
): Promise<SearchEngineEventItem[]> => {
  // One extra, so leaving out the page's own kit still fills the row.
  const params = getSimilarKitsQueryParams({
    category: page.categories.main,
    limit: SIMILAR_KITS_LIMIT + 1,
  })
  const response = await kftApiRequest<EventDescription[]>(
    `v1/store/events?${qs.stringify(params, { encode: false, arrayFormat: 'comma' })}`,
    { method: 'GET' }
  )
  return (transformToAlgoliaEvent(response) as SearchEngineEventItem[])
    .filter((kit) => kit.hashed_id !== page.id)
    .slice(0, SIMILAR_KITS_LIMIT)
}

/**
 * DIY kits for event and kit pages, per the shared search engine (KFT-4115):
 * `algolia` → KFT API, `typesense` → `products_*`. Client-only like the other
 * product showcases — mount it near the viewport (RenderOnIntersection). The
 * engine is in the key, so an A/B force refetches from the other backend.
 */
export const useSimilarKits = (page: SimilarShowcaseSource) => {
  const { locale } = useI18n()
  const engine = useSearchEngine({ clientOnly: true })
  const typesenseHandle = useTypesenseHandle()

  return useAsyncData<SearchEngineEventItem[]>(
    () => `similar-kits-${engine.value}-${page.permalink}`,
    async () => {
      // Both engines stay within the page's categories; none → no row.
      if (!page.permalink || !page.categories.main) return []
      if (engine.value === 'typesense') {
        return fetchSimilarKitsTypesense(
          page,
          getBoxesIndexName(locale.value),
          typesenseHandle
        )
      }
      return fetchSimilarKitsKft(page)
    },
    { server: false, default: () => [], dedupe: 'defer' }
  )
}
